import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Alert,
  AlertDescription,
  Badge,
  FieldHint,
  useToast,
} from "@repo/ui";
import { Building2, Loader2, Lock } from "lucide-react";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { updatePharmacyInfo } from "@/lib/api/pharmacy";
import { ApiClientError } from "@/lib/api/client";

interface PharmacyData {
  legal_name: string;
  dba_name: string | null;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  phone_number: string;
  npi: string;
  timezone: string;
  pharmacy_type: string;
}

interface PharmacyInfoFormProps {
  pharmacy: PharmacyData;
  canEditSettings: boolean;
  onSuccess: () => void;
}

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
];

export function PharmacyInfoForm({
  pharmacy,
  canEditSettings,
  onSuccess,
}: PharmacyInfoFormProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [legalName, setLegalName] = useState(pharmacy.legal_name);
  const [dbaName, setDbaName] = useState(pharmacy.dba_name || "");
  const [streetAddress, setStreetAddress] = useState(pharmacy.street_address);
  const [city, setCity] = useState(pharmacy.city);
  const [state, setState] = useState(pharmacy.state);
  const [zipCode, setZipCode] = useState(pharmacy.zip_code);
  const [phoneNumber, setPhoneNumber] = useState(pharmacy.phone_number);
  const [timezone, setTimezone] = useState(pharmacy.timezone);
  const [pharmacyType, setPharmacyType] = useState(pharmacy.pharmacy_type);

  const hasChanges =
    legalName !== pharmacy.legal_name ||
    dbaName !== (pharmacy.dba_name || "") ||
    streetAddress !== pharmacy.street_address ||
    city !== pharmacy.city ||
    state !== pharmacy.state ||
    zipCode !== pharmacy.zip_code ||
    phoneNumber !== pharmacy.phone_number ||
    timezone !== pharmacy.timezone ||
    pharmacyType !== pharmacy.pharmacy_type;

  const handleSubmit = async (e: React.FormEvent, approvalToken?: string) => {
    e.preventDefault();
    if (!canEditSettings) return;

    setError("");
    setIsLoading(true);

    try {
      await updatePharmacyInfo(
        {
          legal_name: legalName,
          dba_name: dbaName || null,
          street_address: streetAddress,
          city,
          state,
          zip_code: zipCode,
          phone_number: phoneNumber,
          timezone,
          pharmacy_type: pharmacyType,
        },
        { approvalToken }
      );

      toast({
        title: "Changes Saved",
        description: "Pharmacy information has been updated successfully.",
        variant: "success",
      });
      onSuccess();
    } catch (err) {
      if (
        err instanceof ApiClientError &&
        handlePermissionError(err, {
          requestApproval,
          toast,
          retryFn: async (token) => handleSubmit(e, token),
          actionDescription: "Update pharmacy information",
        })
      )
        return;
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setLegalName(pharmacy.legal_name);
    setDbaName(pharmacy.dba_name || "");
    setStreetAddress(pharmacy.street_address);
    setCity(pharmacy.city);
    setState(pharmacy.state);
    setZipCode(pharmacy.zip_code);
    setPhoneNumber(pharmacy.phone_number);
    setTimezone(pharmacy.timezone);
    setPharmacyType(pharmacy.pharmacy_type);
    setError("");
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Pharmacy Information</CardTitle>
              <CardDescription>
                General details about your pharmacy
              </CardDescription>
            </div>
          </div>
          {!canEditSettings && (
            <Badge variant="outline" className="gap-1.5">
              <Lock className="h-3 w-3" />
              Admin Only
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Legal Name & DBA */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="legalName">Legal Name</Label>
              {canEditSettings ? (
                <Input
                  id="legalName"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  disabled={isLoading}
                  required
                />
              ) : (
                <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                  {legalName}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="dbaName">
                  DBA Name{" "}
                  <span className="text-muted-foreground font-normal">
                    (Optional)
                  </span>
                </Label>
                <FieldHint>
                  Doing Business As. Only fill this in if your pharmacy runs
                  under a different trade name than the legal entity name.
                </FieldHint>
              </div>
              {canEditSettings ? (
                <Input
                  id="dbaName"
                  value={dbaName}
                  onChange={(e) => setDbaName(e.target.value)}
                  disabled={isLoading}
                  placeholder="Doing Business As..."
                />
              ) : (
                <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                  {dbaName || "Not set"}
                </p>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <Label>Address</Label>
            <div className="space-y-3">
              {canEditSettings ? (
                <>
                  <Input
                    id="streetAddress"
                    value={streetAddress}
                    onChange={(e) => setStreetAddress(e.target.value)}
                    disabled={isLoading}
                    placeholder="Street Address"
                    required
                  />
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Input
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      disabled={isLoading}
                      placeholder="City"
                      required
                    />
                    <Select
                      value={state}
                      onValueChange={setState}
                      disabled={isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="zipCode"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      disabled={isLoading}
                      placeholder="Zip Code"
                      required
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                  {streetAddress}, {city}, {state} {zipCode}
                </p>
              )}
            </div>
          </div>

          {/* Phone */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              {canEditSettings ? (
                <Input
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={isLoading}
                  placeholder="(555) 123-4567"
                  required
                  data-testid="settings-phone-input"
                />
              ) : (
                <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                  {phoneNumber}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="npi">NPI</Label>
              <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                {pharmacy.npi}
              </p>
              <p className="text-xs text-muted-foreground">
                NPI cannot be changed. Contact support if needed.
              </p>
            </div>
          </div>

          {/* Type & Timezone */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="pharmacyType">Pharmacy Type</Label>
              {canEditSettings ? (
                <Select
                  value={pharmacyType}
                  onValueChange={setPharmacyType}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="independent">Independent</SelectItem>
                    <SelectItem value="chain_location">
                      Chain Location
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                  {pharmacyType === "independent"
                    ? "Independent"
                    : "Chain Location"}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              {canEditSettings ? (
                <Select
                  value={timezone}
                  onValueChange={setTimezone}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                  {TIMEZONES.find((tz) => tz.value === timezone)?.label ||
                    timezone}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          {canEditSettings && (
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={isLoading || !hasChanges}
              >
                Discard Changes
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !hasChanges}
                data-testid="settings-pharmacy-save"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
